import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { verifyAccount, resendVerificationCode, acceptRecipientInvite, setUserType } from '../../services/api';
import { useAuth } from './AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import { Mail, CheckCircle2, AlertCircle, ArrowRight, ShieldCheck } from 'lucide-react';
import '../auth/AuthForms.scss';

const VerificationForm = () => {
  const { t } = useTranslation(['auth', 'common']);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [isResending, setIsResending] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isVerified, setIsVerified] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { login } = useAuth();
  
  let storedVerification = {};
  try {
    storedVerification = JSON.parse(sessionStorage.getItem('foth_pending_verification') || '{}');
  } catch (err) {
    sessionStorage.removeItem('foth_pending_verification');
  }

  const { userId: stateUserId, email: stateEmail, role: stateRole, inviteToken: stateInviteToken } = location.state || {};
  const userId = stateUserId || storedVerification.userId;
  const email = stateEmail || storedVerification.email;
  const role = stateRole || storedVerification.role;
  const inviteToken = stateInviteToken || storedVerification.inviteToken;
  
  useEffect(() => {
    const timer = setTimeout(() => setIsLoading(false), 600);
    return () => clearTimeout(timer);
  }, []);
  
  const VerificationSchema = Yup.object().shape({
    code: Yup.string()
      .length(6, t('errors.password_min')) // Reuse or add specific key
      .required(t('common:required')),
  });

  if (isLoading) {
    return (
      <div className="auth-loading">
        <motion.div 
          animate={{ scale: [1, 1.1, 1], opacity: [0.5, 1, 0.5] }}
          transition={{ repeat: Infinity, duration: 2 }}
          className="loading-logo"
        >
          FOTH
        </motion.div>
      </div>
    );
  }
  
  if (!userId) {
    return (
      <div className="auth-page editorial-split">
        <div className="auth-form-side" style={{ flex: 1 }}>
          <div className="auth-container">
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="auth-card-modern text-center"
            >
               <div className="alert-premium error">
                  <AlertCircle size={24} />
                  <div>
                    <h3 style={{ margin: 0 }}>{t('common:error')}</h3>
                    <p style={{ margin: 0, fontSize: '14px' }}>No session found for verification.</p>
                  </div>
               </div>
               <Link to="/login" className="auth-submit-btn-premium mt-8">
                  {t('login.submit')}
               </Link>
            </motion.div>
          </div>
        </div>
      </div>
    );
  }

  const handleVerify = async (values, { setSubmitting }) => {
    try {
      setError(null);
      const response = await verifyAccount(userId, values.code);
      
      await login(null, response.data.user);
      if (role) {
        await setUserType(role);
      }
      sessionStorage.removeItem('foth_pending_registration');
      sessionStorage.removeItem('foth_pending_verification');

      let target = '/complete-profile';
      if (inviteToken && role === 'creator') {
        const accepted = await acceptRecipientInvite(inviteToken);
        target = `/creator/invited/${accepted.data.offer_id}`;
        sessionStorage.removeItem('foth_invite_return_to');
      }

      // Trigger the welcome animation only after the invitation lifecycle is complete.
      setIsVerified(true);
      setTimeout(() => {
        navigate(target, { state: { role } });
      }, 3500);
    } catch (error) {
      console.error('Verification error:', error);
      setError(error.response?.data?.error || t('errors.generic_error'));
      setSubmitting(false);
    }
  };

  const handleResendCode = async () => {
    setIsResending(true);
    setError(null);
    try {
      await resendVerificationCode(userId);
      setSuccess(t('verification.resend.success'));
      setTimeout(() => setSuccess(null), 5000);
    } catch (error) {
      setError(t('errors.connection_error'));
    } finally {
      setIsResending(false);
    }
  };

  return (
    <div className="auth-page editorial-split">
      <AnimatePresence>
        {isVerified && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="welcome-ritual-overlay"
          >
             <motion.div 
               initial={{ scale: 0.8, opacity: 0 }}
               animate={{ scale: 1, opacity: 1 }}
               transition={{ delay: 0.3, duration: 0.8 }}
               className="ritual-content"
             >
                <div className="ritual-icon">
                   <ShieldCheck size={64} />
                </div>
                <motion.h2
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.8 }}
                >
                  {t('verification.ritual.authenticated')}
                </motion.h2>
                <motion.h1
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 1.2 }}
                >
                  {t('verification.ritual.welcome')}
                </motion.h1>
                <motion.div 
                  className="ritual-loader"
                  initial={{ width: 0 }}
                  animate={{ width: '100%' }}
                  transition={{ delay: 1.8, duration: 1.5 }}
                />
                <p>{t('verification.ritual.unlocking')}</p>
             </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Editorial Left Side */}
      <div className="auth-editorial-side">
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1.5 }}
            className="editorial-bg-overlay"
            style={{ backgroundImage: "url('/luxury_auth_bg_1.png')" }}
          />
          <div className="editorial-content">
            <Link to="/" className="auth-logo-inverse">FOTH</Link>
            <div className="editorial-text">
               <h1>{t('verification.editorial.title')}</h1>
               <p>{t('verification.editorial.text')}</p>
            </div>
          </div>
      </div>

      {/* Form Right Side */}
      <div className="auth-form-side">
        <div className="auth-container">
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="auth-card-modern"
          >
            <div className="auth-header">
              <h2 className="auth-title">{t('verification.title')}</h2>
              <p className="auth-subtitle">{t('verification.subtitle')}</p>
            </div>
          
            <div className="auth-body">
              <div className="info-badge">
                <Mail size={18} />
                <span>
                  <Trans 
                    i18nKey="auth:verification.info" 
                    values={{ email }} 
                    components={{ bold: <strong /> }}
                  />
                </span>
              </div>
              
              <AnimatePresence>
                {error && (
                  <motion.div 
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="alert-premium error"
                  >
                    <AlertCircle size={18} /> {error}
                  </motion.div>
                )}
                
                {success && (
                  <motion.div 
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="alert-premium success"
                  >
                    <CheckCircle2 size={18} /> {success}
                  </motion.div>
                )}
              </AnimatePresence>
              
              <Formik
                initialValues={{ code: '' }}
                validationSchema={VerificationSchema}
                onSubmit={handleVerify}
              >
                {({ isSubmitting, values, setFieldValue }) => (
                  <Form className="auth-form">
                    <div className="form-group-full">
                      <label className="form-label">{t('verification.form.label')}</label>
                      <div className="otp-container">
                         <Field
                          type="text"
                          name="code"
                          className="form-control-premium otp-input"
                          placeholder={t('verification.form.placeholder')}
                          maxLength="6"
                          autoComplete="one-time-code"
                        />
                      </div>
                      <ErrorMessage name="code" component="div" className="error-message" />
                    </div>

                    <div className="form-actions-stack">
                      <motion.button
                        type="submit"
                        className="auth-submit-btn-premium"
                        disabled={isSubmitting || values.code.length !== 6}
                        whileTap={{ scale: 0.98 }}
                      >
                        {isSubmitting ? <span className="btn-spinner" /> : <>{t('verification.submit')} <ArrowRight size={18} /></>}
                      </motion.button>
                      
                      <button 
                        type="button" 
                        onClick={handleResendCode}
                        disabled={isResending}
                        className="btn-text-link"
                      >
                        {isResending ? t('verification.resend.loading') : (
                          <>{t('verification.resend.prompt')} <span>{t('verification.resend.link')}</span></>
                        )}
                      </button>
                    </div>
                  </Form>
                )}
              </Formik>
            </div>
            
            <div className="auth-footer-minimal">
              <p>{t('register.footer.has_account')} <Link to="/login">{t('register.footer.login_link')}</Link></p>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default VerificationForm;
